import './PaymentsPage.css';
import InvoiceLink from './InvoiceLink';
import {InvoicePaymentsLink} from './InvoicesPage';
import React, { useState, useMemo } from 'react';
import ProjectModal from './ProjectModal';
import ProjectDetailModal from './ProjectDetailModal';
import ExportButtons from './ExportButtons';
import ImportModal from './ImportModal';
import {
  projectPaymentSummary, projectAggregateStatus,
  daysOverdue, effectiveDueDate,
} from '../paymentsUtil';

// Shared pill style for inline status badges inside invoice cells.
const PILL_STYLE = {
  fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 3,
  whiteSpace: 'nowrap', display: 'inline-block',
};

function statusBadge(status) {
  const map = { Active: 'badge-active', Completed: 'badge-completed', Pending: 'badge-pending', Rejected: 'badge-rejected', Disputed: 'badge-disputed' };
  return <span className={`badge ${map[status] || ''}`}>{status}</span>;
}

const fmt = n => n != null && n !== '' ? `£${Number(n).toLocaleString('en-GB', { minimumFractionDigits: 0 })}` : '—';
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';

// One cell describing one invoice's current state. `suppressIssue` is set
// when there's no money left to chase on the project (pending = 0) — in that
// case we don't pester with "Issue invoice" prompts on empty invoice slots.
function InvoiceStatusContent({ inv, isFirst, projectHasInv1, suppressIssue }) {
  if(inv.status==='not-required')return <span style={{color:'var(--text3)',fontSize:12}}>Not required</span>;
  if(inv.status==='payment-recorded')return <div style={{fontSize:12}}>{!inv.no&&<div>No invoice info</div>}<span style={{color:'var(--green)'}}>Payment recorded · {fmt(inv.paid)}</span></div>;
  if (inv.status === 'not-issued') {
    if (suppressIssue) return <span style={{ color: 'var(--text3)', fontSize: 12 }}>No invoice info</span>;
    if (isFirst) {
      return <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', background: 'var(--red)', padding: '2px 7px', borderRadius: 4, whiteSpace: 'nowrap' }}>Issue invoice</span>;
    }
    // Inv 2: prompt only once it's time (inv 1 + project's schedule, default 3 mo).
    // Before that, show a subtle hint so Cagri knows when to come back.
    if (projectHasInv1) {
      if (inv.isTimeToIssue) {
        return <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', background: 'var(--orange)', padding: '2px 7px', borderRadius: 4, whiteSpace: 'nowrap' }}>Issue invoice</span>;
      }
      if (inv.expectedIssueDate) {
        return <span style={{ color: 'var(--text3)', fontSize: 11 }} title={`Scheduled for ${inv.scheduleMonths}mo after invoice 1`}>Expected {fmtDate(inv.expectedIssueDate)}</span>;
      }
    }
    return <div style={{color:'var(--text2)',fontSize:12}}>No invoice info{inv.amount!=null?' · '+fmt(inv.amount):''}<small style={{display:'block'}}>Issue date not set</small></div>;
  }
  const dueDisplay = effectiveDueDate(inv.date, inv.due);
  if (inv.status === 'paid') {
    return (
      <div style={{ fontSize: 12 }}>
        <div style={{ color: 'var(--text)', marginBottom: 3 }}>{!inv.linkedInvoice&&(inv.no||'No invoice info')}{inv.no&&!inv.date&&<small style={{display:'block',color:'var(--text2)'}}>No invoice date</small>}</div>
        <span style={{ ...PILL_STYLE, color: 'var(--green)', background: 'var(--green-dim)' }}>
          Paid{inv.projectPaid?' · project settled':inv.paid!=null?' · '+fmt(inv.paid):''}
        </span>
      </div>
    );
  }
  if (inv.status === 'overdue') {
    const d = daysOverdue(dueDisplay);
    return (
      <div style={{ fontSize: 12 }}>
        <div style={{ color: 'var(--text)', marginBottom: 3 }}>{!inv.linkedInvoice&&(inv.no||'No invoice info')}{inv.no&&!inv.date&&<small style={{display:'block',color:'var(--text2)'}}>No invoice date</small>}</div>
        <span style={{ ...PILL_STYLE, color: 'var(--red)', background: 'var(--red-dim)' }}>
          Overdue {d}d
        </span>
      </div>
    );
  }
  if(inv.status==='unverified')return <span style={{color:'var(--orange)'}}>Verify invoice total</span>;
  if(inv.status==='partial')return <div>{!inv.no&&<small>No invoice info<br/></small>}<span style={{color:'var(--orange)'}}>Partial · {fmt(inv.paid)} paid</span></div>;
  // pending — issued but unpaid, due-date may or may not be known
  return (
    <div style={{ fontSize: 12 }}>
      <div style={{ color: 'var(--text)', marginBottom: 3 }}>{!inv.linkedInvoice&&(inv.no||'No invoice info')}{inv.no&&!inv.date&&<small style={{display:'block',color:'var(--text2)'}}>No invoice date</small>}</div>
      <span style={{ ...PILL_STYLE, color: 'var(--blue)', background: 'var(--blue-dim)' }}>
        {dueDisplay ? `Due ${fmtDate(dueDisplay)}` : 'Pending'}
      </span>
    </div>
  );
}

export function InvoiceStatusCell({inv,isFirst,projectHasInv1,suppressIssue}){
 const due=effectiveDueDate(inv.date,inv.due);
 let label=inv.status==='paid'?'Paid':inv.status==='overdue'?`${daysOverdue(due)}d overdue`:inv.status==='partial'?'Partial':inv.status==='payment-recorded'?'Payment recorded':inv.status==='unverified'?'Verify amount':inv.status==='not-required'?'Not required':inv.status==='pending'?(due?'Due '+fmtDate(due):'Pending'):'';
 if(inv.status==='not-issued')label=suppressIssue?'No invoice info':isFirst||inv.isTimeToIssue?'Issue invoice':projectHasInv1&&inv.expectedIssueDate?'Expected '+fmtDate(inv.expectedIssueDate):'Not issued';
 return <div className="payment-invoice"><div>{inv.linkedInvoice?<InvoiceLink invoice={inv.linkedInvoice}/>:inv.no||null}</div><span className={'payment-invoice-state state-'+inv.status} title={inv.paid!=null?'Paid '+fmt(inv.paid):undefined}>{label}</span></div>;
}
function AdditionalInvoices({invoices,hasSecond}){if(!invoices.length)return null;return hasSecond?<details className="payment-extra" onClick={e=>e.stopPropagation()}><summary>+{invoices.length} invoice{invoices.length>1?'s':''}</summary>{invoices.map(d=><div key={d.id}><InvoiceLink invoice={d}/></div>)}</details>:<div className="payment-invoice">{invoices.map(d=><div key={d.id}><InvoiceLink invoice={d}/><small>{fmtDate(d.date)}</small></div>)}</div>;}

function SortTh({ label, sortKey, sortState, onSort }) {
  const [key, dir] = sortState;
  const active = key === sortKey;
  return (
    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => onSort(sortKey)}>
      {label} <span style={{ color: active ? 'var(--accent)' : 'var(--text3)', fontSize: 10 }}>{active ? (dir === 'asc' ? '▲' : '▼') : '⇅'}</span>
    </th>
  );
}

function sortKey(p) {
  if (p.order_date) return p.order_date;
  // derive date from project code e.g. RP-22001 → 2022
  const match = p.project_code?.match(/(\d{2})(\d+)$/);
  if (match) return `20${match[1]}-01-01`;
  return '1970-01-01';
}

export default function PaymentsPage({ projects, customers, onRefresh, user }) {
  const [search, setSearch] = useState('');
  const [payFilter, setPayFilter] = useState('');
  const [serviceFilter, setServiceFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [showClosed, setShowClosed] = useState(false);
  const [sort, setSort] = useState(['date', 'desc']);
  const [detailModal, setDetailModal] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [importing, setImporting] = useState(false);

  const handleSort = (key) => setSort(([k, d]) => k === key ? [key, d === 'asc' ? 'desc' : 'asc'] : [key, 'asc']);

  const services = [...new Set(projects.map(p => p.service_type).filter(Boolean))].sort();
  const owners = [...new Set(projects.map(p => p.created_by_name).filter(Boolean))].sort();

  const enriched = useMemo(() => projects
    .filter(p => p.status !== 'Rejected')
    .map(p => {
      const summary = projectPaymentSummary(p);
      const agg = projectAggregateStatus(p);
      // Don't pester with "Issue invoice" when there's nothing to chase
      // (paid, no fee tracked, or the project is in dispute).
      const suppressIssue = summary.pending <= 0
        || summary.agreed <= 0
        || p.status === 'Disputed';
      return {
        ...p,
        _summary: summary,
        _aggKey: agg.key,
        _aggLabel: agg.label,
        _agreed: summary.agreed,
        _paid: summary.paid,
        _pending: summary.pending,
        _overdue: summary.overdue,
        _suppressIssue: suppressIssue,
        _sortDate: sortKey(p),
      };
    }), [projects]);

  const filtered = useMemo(() => {
    let list = enriched.filter(p => {
      const cust = customers.find(c => c.id === p.customer_id);
      const q = search.toLowerCase();
      const match = `${p.project_code} ${p.project_name} ${p.council || ''} ${cust?.full_name || ''} ${cust?.company || ''}`.toLowerCase().includes(q);
      const payMatch = !payFilter || p._aggKey === payFilter;
      const serviceMatch = !serviceFilter || p.service_type === serviceFilter;
      const ownerMatch = !ownerFilter || p.created_by_name === ownerFilter;
      // Hide finished projects (paid, disputed, or no fee tracked) unless
      // explicitly asked for, or the user picked the matching filter.
      const isClosed = p._aggKey === 'paid' || p._aggKey === 'disputed' || p._aggKey === 'none';
      const closedHidden = !showClosed && payFilter !== 'paid' && payFilter !== 'disputed' && isClosed;
      return match && payMatch && serviceMatch && ownerMatch && !closedHidden;
    });

    const [key, dir] = sort;
    list.sort((a, b) => {
      let av, bv;
      if (key === 'date') { av = a._sortDate; bv = b._sortDate; }
      else if (key === 'agreed') { av = a._agreed; bv = b._agreed; }
      else if (key === 'paid') { av = a._paid; bv = b._paid; }
      else if (key === 'pending') { av = a._pending; bv = b._pending; }
      else if (key === 'customer') {
        const ca = customers.find(c => c.id === a.customer_id);
        const cb = customers.find(c => c.id === b.customer_id);
        av = ca?.full_name || ''; bv = cb?.full_name || '';
      }
      else { av = a[key] || ''; bv = b[key] || ''; }
      if (av < bv) return dir === 'asc' ? -1 : 1;
      if (av > bv) return dir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [enriched, customers, search, payFilter, serviceFilter, ownerFilter, sort, showClosed]);

  const totalAgreed = filtered.reduce((s, p) => s + p._agreed, 0);
  const totalPaid = filtered.reduce((s, p) => s + p._paid, 0);
  const totalOverdue = filtered.reduce((s, p) => s + p._overdue, 0);
  const overdueCount = filtered.filter(p => p._aggKey === 'overdue').length;
  const partialCount = filtered.filter(p => p._aggKey === 'partial').length;
  const hiddenClosedCount = enriched.filter(p => p._aggKey === 'paid' || p._aggKey === 'disputed' || p._aggKey === 'none').length;

  const custName = (id) => { const c = customers.find(c => c.id === id); return c ? c.full_name : '—'; };

  return (
    <div style={{ padding: '28px 32px' }}>
      {user?.id==='6870263b-7758-4486-b372-d7fda5c998b3'&&<InvoicePaymentsLink/>}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, marginBottom: 4 }}>Payments</h1>
          <p style={{ color: 'var(--text3)', fontSize: 13 }}>{filtered.length} projects · {overdueCount} overdue · {partialCount} partial</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn-ghost" style={{ fontSize: 12, padding: '8px 14px' }} onClick={() => setImporting(true)}>↑ Import</button>
          <ExportButtons
          rows={filtered}
          columns={[
            { label: 'Code', get: p => p.project_code },
            { label: 'Project', get: p => p.project_name },
            { label: 'Council', get: p => p.council || '' },
            { label: 'Customer', get: p => { const c = customers.find(c => c.id === p.customer_id); return c ? c.full_name : ''; } },
            { label: 'Owner', get: p => p.created_by_name },
            { label: 'Service', get: p => p.service_type },
            { label: 'Status', get: p => p.status },
            { label: 'Tender Fee', get: p => p.financials?.[0]?.tender_fee },
            { label: 'Agreed Fee', get: p => p._agreed },
            { label: 'Paid', get: p => p._paid },
            { label: 'Pending', get: p => p._pending },
            { label: 'Invoice 1', get: p => p.financials?.[0]?.invoice_no_1 },
            { label: 'Invoice Date 1', get: p => p.financials?.[0]?.invoice_date_1 },
            { label: 'Invoice 2', get: p => p.financials?.[0]?.invoice_no_2 },
            { label: 'Invoice Date 2', get: p => p.financials?.[0]?.invoice_date_2 },
            { label: 'Payment Status', get: p => p._payStatus },
            { label: 'Order Date', get: p => p.order_date },
          ]}
          title="Payments"
          filename="liongate_payments"
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
        <div className="stat-card">
          <div className="stat-label">Total Agreed</div>
          <div className="stat-value" style={{ color: 'var(--accent)' }}>{fmt(totalAgreed)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Collected</div>
          <div className="stat-value" style={{ color: 'var(--green)' }}>{fmt(totalPaid)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Balance</div>
          <div className="stat-value" style={{ color: 'var(--red)' }}>{fmt(Math.max(0, totalAgreed - totalPaid))}</div>
          <div className="stat-sub">agreed − collected</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Overdue</div>
          <div className="stat-value" style={{ color: 'var(--red)' }}>{fmt(totalOverdue)}</div>
          <div className="stat-sub">{overdueCount} project{overdueCount === 1 ? '' : 's'} past due</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <div className="search-bar" style={{ flex: 1, minWidth: 200 }}>
          <span className="icon">⌕</span>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by code, name, customer, council..." />
        </div>
        <select value={payFilter} onChange={e => setPayFilter(e.target.value)} style={{ width: 180 }}>
          <option value="">All payments</option>
          <option value="overdue">Overdue</option>
          <option value="pending">Pending (not yet due)</option>
          <option value="partial">Partial</option>
          <option value="paid">Paid</option>
          <option value="unbilled">Unbilled</option>
        </select>
        <select value={serviceFilter} onChange={e => setServiceFilter(e.target.value)} style={{ width: 150 }}>
          <option value="">All services</option>
          {services.map(s => <option key={s}>{s}</option>)}
        </select>
        <select value={ownerFilter} onChange={e => setOwnerFilter(e.target.value)} style={{ width: 150 }}>
          <option value="">All owners</option>
          {owners.map(o => <option key={o}>{o}</option>)}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text2)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
          <input type="checkbox" checked={showClosed} onChange={e => setShowClosed(e.target.checked)} style={{ margin: 0 }} />
          <span>Show closed ({hiddenClosedCount})</span>
        </label>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'auto' }}>
        {filtered.length === 0 ? (
          <div className="empty-state">
            <div className="icon">💷</div>
            <p>No projects found</p>
          </div>
        ) : (
          <table className="table payments-compact">
            <thead>
              <tr>
                <SortTh label="Code" sortKey="project_code" sortState={sort} onSort={handleSort} />
                <SortTh label="Project" sortKey="project_name" sortState={sort} onSort={handleSort} />
                <SortTh label="Customer" sortKey="customer" sortState={sort} onSort={handleSort} />
                <SortTh label="Council" sortKey="council" sortState={sort} onSort={handleSort} />
                <SortTh label="Owner" sortKey="created_by_name" sortState={sort} onSort={handleSort} />
                <th>Service</th>
                <SortTh label="Status" sortKey="status" sortState={sort} onSort={handleSort} />
                <SortTh label="Tender" sortKey="tender_fee" sortState={sort} onSort={handleSort} />
                <SortTh label="Agreed" sortKey="agreed" sortState={sort} onSort={handleSort} />
                <SortTh label="Paid" sortKey="paid" sortState={sort} onSort={handleSort} />
                <SortTh label="Pending" sortKey="pending" sortState={sort} onSort={handleSort} />
                <th>Invoice 1</th>
                <th>Invoice 2</th>
                <th>Schedule</th>
                <SortTh label="Date" sortKey="date" sortState={sort} onSort={handleSort} />
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => {
                const fin = p.financials?.[0];
                const extraInvoices=p.linkedInvoices?.filter(d=>![p._summary.inv1.no,p._summary.inv2.no].includes(d.number))||[];
                return (
                  <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => setDetailModal(p)}>
                    <td><span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--accent)' }}>{p.project_code}</span></td>
                    <td className="td-main" style={{ maxWidth: 180 }}>
                      <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.project_name}</div>
                    </td>
                    <td style={{ maxWidth: 140 }}>
                      <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 13 }}>{custName(p.customer_id)}</div>
                    </td>
                    <td style={{ maxWidth: 150 }} title={p.council || undefined}><div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 13 }}>{p.council || '—'}</div></td>
                    <td style={{ fontSize: 12, color: 'var(--text2)' }}>{p.created_by_name || '—'}</td>
                    <td><span style={{ fontSize: 12, background: 'var(--bg3)', padding: '2px 8px', borderRadius: 4 }}>{p.service_type}</span></td>
                    <td>{statusBadge(p.status)}</td>
                    <td style={{ color: 'var(--text2)' }}>{fmt(fin?.tender_fee)}</td>
                    <td style={{ color: 'var(--text)', fontWeight: 500 }}>{fmt(fin?.agreed_fee)}</td>
                    <td style={{ color: 'var(--green)' }}>{fmt(fin?.paid_amount)}</td>
                    <td style={{ color: p._pending > 0 ? 'var(--red)' : 'var(--text3)', fontWeight: p._pending > 0 ? 600 : 400 }}>{fmt(p._pending)}</td>
                    <td style={{ fontSize: 12 }}>
                      <InvoiceStatusCell inv={{...p._summary.inv1,linkedInvoice:p.linkedInvoices?.find(d=>d.number===p._summary.inv1.no)}} isFirst={true} projectHasInv1={true} suppressIssue={p._suppressIssue} />
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {(p._summary.inv2.no||!extraInvoices.length)&&<InvoiceStatusCell inv={{...p._summary.inv2,linkedInvoice:p.linkedInvoices?.find(d=>d.number===p._summary.inv2.no)}} isFirst={false} projectHasInv1={p._summary.inv1.status !== 'not-issued'} suppressIssue={p._suppressIssue} />}
                      <AdditionalInvoices invoices={extraInvoices} hasSecond={!!p._summary.inv2.no}/>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text3)' }} title={`Invoice 2 is scheduled for ${p._summary.inv2.scheduleMonths} month${p._summary.inv2.scheduleMonths === 1 ? '' : 's'} after Invoice 1`}>
                      {p._summary.inv2.scheduleMonths} mo
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text3)' }}>{fmtDate(p.order_date)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {importing && <ImportModal type="payments" onClose={() => setImporting(false)} onDone={() => { setImporting(false); onRefresh(); }} />}
      {detailModal && (
        <ProjectDetailModal
          project={detailModal}
          customers={customers}
          onClose={() => setDetailModal(null)}
          onRefresh={() => { setDetailModal(null); onRefresh(); }}
          user={user}
        />
      )}
      {editModal && (
        <ProjectModal project={editModal} customers={customers} onClose={() => setEditModal(null)} onSaved={() => { setEditModal(null); onRefresh(); }} user={user} />
      )}
    </div>
  );
}
